/**
 * Avatar stage — the ONE persistent home of the agent's media elements
 * (mobile UX program, M4).
 *
 * Problem this solves: `VideoGreeter` used to be mounted twice (big +
 * mini islands), EACH owning its own greeter `<video>` + live Simli
 * `<video>`. Moving the face between surfaces meant unmount/remount:
 * the greeter clip re-`load()`ed, the live `MediaStream` re-attached and
 * re-ran the `liveReady` first-frame handshake (a visible dead-frame gap
 * — exactly what the summon overlay exists to prevent), and autoplay
 * races re-armed. The mobile program adds MORE surfaces (teaser avatar,
 * co-pilot PiP) and moves the face between them mid-session.
 *
 * Decision (plan §4.1): a single stage element that owns BOTH `<video>`s,
 * created ONCE, re-parented between dock anchors via appendChild. Media
 * elements survive re-parenting (unlike iframes); decode can hiccup, so
 * `dock()` reuses the exact recovery already proven in the tab-visibility
 * handler: re-assign `srcObject`, `void play().catch(...)`.
 *
 * INVARIANTS (plan §4.1.1, I1–I3):
 *   - `liveReady` is NOT reset on dock moves — only when the stream
 *     itself ends (`liveStream` → null).
 *   - The `<video>` elements live OUTSIDE Preact and are never recreated
 *     by a re-render. Preact renders the chrome AROUND each dock
 *     (summon, glow, chip, controls, status) — see `video-greeter.tsx`.
 *   - Every media-lifecycle behavior here is MOVED VERBATIM from the
 *     old VideoGreeter effects (src swap, speaking-pause, stream attach
 *     + rVFC handshake, visibility resume) — zero behavior change.
 */

import { effect, type Signal } from '@preact/signals';
import { loadBoundless } from './boundless-loader';
import type { KeyerHandle } from './boundless/keyer';

export type AvatarDockName = 'mini' | 'big' | 'teaser' | 'pip' | 'launcher';

export interface AvatarStageBits {
  /** Server-resolved greeter clip URL (null → no greeter video). */
  greeterUrl: Signal<string | null>;
  /** TTS playback active — pauses the greeter loop (its own voice would double). */
  speaking: Signal<boolean>;
  /** Live Simli avatar stream during a voice call. */
  liveStream: Signal<MediaStream | null>;
  /** Set by the stage when the live video PAINTS its first frame. */
  liveReady: Signal<boolean>;
  /**
   * Phase 3 battery hygiene — external pause for the greeter loop
   * (launcher offscreen / ~30s idle). Optional; resumes on false.
   */
  greeterPaused?: Signal<boolean>;
  /**
   * Key the live stream in the box (src/live-key.ts): true when the
   * persona is Beam-minted (green backdrop) and the current view is not
   * Beam. The keyer lives in the lazy Beam chunk; a non-green backdrop
   * makes it return null and the raw stream shows as before.
   */
  keyLive?: Signal<boolean>;
}

export interface AvatarStage {
  /** The stage element — owns the two `<video>`s. Position: absolute inset 0. */
  el: HTMLElement;
  /** Register (or replace) a dock anchor. If it's the active dock, (re)attach. */
  registerDock(name: AvatarDockName, anchor: HTMLElement): void;
  unregisterDock(name: AvatarDockName): void;
  /** Re-parent the stage into the named dock. No-op when already there. */
  dock(name: AvatarDockName): void;
  activeDock(): AvatarDockName | null;
  /** Tear down listeners/effects (tests; production stages live for the page). */
  dispose(): void;
}

/**
 * Per-dock class for the GREETER video. In the big dock it carries the
 * legacy `sp-big-avatar-video` class (absolute inset-0 + the initialized
 * top-shift + voice-mode hide rules key on it); in the mini dock it is
 * intentionally CLASSLESS — mini sizing comes from the `.sp-mini-avatar
 * video` descendant selector, and the shipped voice-mode hide rule does
 * not catch the mini greeter (preserved as-is, dark slice).
 */
const GREETER_CLASS: Record<AvatarDockName, string> = {
  big: 'sp-big-avatar-video',
  mini: '',
  teaser: '', // sized by `.sp-*-avatar video` descendant rules (Phase 2)
  pip: '',
  launcher: '', // sized by .sp-launcher-avatar descendant rules (Phase 3)
};

export function createAvatarStage(bits: AvatarStageBits): AvatarStage {
  const el = document.createElement('div');
  el.className = 'sp-avatar-stage';
  // Geometry: fill whatever dock we're in. Both containers are
  // position:relative; today's videos were absolute-inset-0 (big) /
  // 100%×100% flow (mini) — an inset wrapper reproduces both.
  el.style.cssText = 'position:absolute;inset:0;';

  const greeterVideo = document.createElement('video');
  greeterVideo.playsInline = true;
  greeterVideo.muted = true;
  greeterVideo.loop = true;
  greeterVideo.autoplay = true;

  const liveVideo = document.createElement('video');
  // Dedicated class (NOT sp-*-avatar-video) so the voice-mode greeter-hide
  // rule doesn't catch it — the live face must stay visible while speaking.
  liveVideo.className = 'sp-avatar-live-video';
  liveVideo.playsInline = true;
  liveVideo.muted = true; // bot audio plays via the Daily audio sink
  liveVideo.autoplay = true;

  // The same stream, blurred, behind the face: the live stream is a 2:3
  // portrait and the big stage is a wide box, so the face video renders
  // narrower than the box with the camera "pulled back" (David,
  // 2026-10-21: the whole head, not a taller box) and this fills the
  // sides. Shown only in the big stage (styles.ts); off when keyed.
  const liveBackdrop = document.createElement('video');
  liveBackdrop.className = 'sp-avatar-live-backdrop';
  liveBackdrop.playsInline = true;
  liveBackdrop.muted = true;
  liveBackdrop.autoplay = true;
  liveBackdrop.setAttribute('aria-hidden', 'true');
  // The frame's ASPECT drives the framing (styles.ts): a portrait stream
  // (Anam cara-4 renders 2:3) gets the pulled-back treatment with the
  // blurred fill behind it; a landscape or square stream fills the box
  // plainly. Decided from the stream itself, so a provider or a Railway
  // ANAM_VIDEO_WIDTH/HEIGHT change needs no widget change.
  const syncAspect = (): void => {
    const w = liveVideo.videoWidth;
    const h = liveVideo.videoHeight;
    el.classList.toggle('sp-avatar-stage--portrait', h > w);
    el.classList.toggle('sp-avatar-stage--landscape', w > 0 && h <= w);
  };
  liveVideo.addEventListener('loadedmetadata', syncAspect);
  liveVideo.addEventListener('resize', syncAspect);
  const bindLive = (stream: MediaStream | null): void => {
    if (liveVideo.srcObject !== stream) liveVideo.srcObject = stream;
    if (liveBackdrop.srcObject !== stream) liveBackdrop.srcObject = stream;
    if (stream) {
      playSafe(liveVideo);
      playSafe(liveBackdrop);
    }
  };

  // Keyed rendition of the live stream (Beam-minted persona in a boxed
  // view). Hidden until a keyer is actually running.
  const liveCanvas = document.createElement('canvas');
  liveCanvas.className = 'sp-avatar-live-canvas';
  liveCanvas.style.display = 'none';

  el.appendChild(greeterVideo);
  el.appendChild(liveBackdrop);
  el.appendChild(liveVideo);
  el.appendChild(liveCanvas);

  let keyer: KeyerHandle | null = null;
  let keyAttempt = 0;
  const stopKeyer = (): void => {
    keyer?.stop();
    keyer = null;
    keyAttempt += 1;
    liveCanvas.style.display = 'none';
    liveVideo.style.opacity = '';
    el.classList.remove('sp-avatar-stage--keyed');
  };
  // Start keying once the stream has frames (the keyer samples the
  // backdrop from the first frame it sees). Any failure = raw stream.
  const startKeying = (): void => {
    if (keyer || bits.keyLive?.value !== true) return;
    const attempt = ++keyAttempt;
    const go = (): void => {
      if (attempt !== keyAttempt || bits.liveStream.value === null) return;
      if (liveVideo.videoWidth === 0) {
        liveVideo.addEventListener('playing', go, { once: true });
        return;
      }
      void loadBoundless()
        .then((m) => {
          if (attempt !== keyAttempt || bits.liveStream.value === null || !m.startKeyer) return;
          const h = m.startKeyer(liveVideo, liveCanvas, { headTarget: null });
          if (!h) return; // WebGL missing or backdrop not green — raw stream
          keyer = h;
          liveCanvas.style.display = '';
          liveVideo.style.opacity = '0'; // keeps playing; the keyer reads its frames
          el.classList.add('sp-avatar-stage--keyed');
        })
        .catch(() => undefined);
    };
    go();
  };

  const docks = new Map<AvatarDockName, HTMLElement>();
  let active: AvatarDockName | null = null;
  const disposers: Array<() => void> = [];

  const playSafe = (v: HTMLVideoElement): void => {
    try {
      void Promise.resolve(v.play()).catch(() => {});
    } catch {
      /* ignore — autoplay races; the stream flows regardless */
    }
  };

  // ---- greeter src swap (verbatim from VideoGreeter) ----
  disposers.push(
    effect(() => {
      const url = bits.greeterUrl.value;
      if (url && greeterVideo.src !== url) {
        greeterVideo.src = url;
        greeterVideo.load();
      }
    }),
  );

  // ---- speaking-pause (verbatim) + Phase-3 battery pause ----
  disposers.push(
    effect(() => {
      const speaking = bits.speaking.value || bits.greeterPaused?.value === true;
      if (!bits.greeterUrl.value) return;
      if (speaking) {
        try {
          greeterVideo.pause();
        } catch {
          /* ignore */
        }
      } else {
        playSafe(greeterVideo);
      }
    }),
  );

  // ---- media visibility (was inline show/hide in the component) ----
  //   live stream → live video owns the surface; greeter hidden
  //   speaking    → greeter hidden (visualizer takes over, chrome-side)
  disposers.push(
    effect(() => {
      const hasLive = bits.liveStream.value !== null;
      const showGreeter = !!bits.greeterUrl.value && !bits.speaking.value && !hasLive;
      greeterVideo.style.display = showGreeter ? '' : 'none';
      liveVideo.style.display = hasLive ? '' : 'none';
    }),
  );

  // ---- live stream attach + first-frame handshake (verbatim) ----
  let cleanupLive: (() => void) | null = null;
  disposers.push(
    effect(() => {
      const stream = bits.liveStream.value;
      cleanupLive?.();
      cleanupLive = null;
      if (stream) {
        bindLive(stream);
        stopKeyer();
        startKeying();
        // Signal first PAINTED frame (not just track-bound) so the summon
        // holds until the face actually renders. rVFC is the precise
        // signal; 'playing'/'loadeddata' are the fallback.
        let done = false;
        const markReady = (): void => {
          if (done) return;
          done = true;
          bits.liveReady.value = true;
        };
        const vAny = liveVideo as HTMLVideoElement & {
          requestVideoFrameCallback?: (cb: () => void) => number;
        };
        if (typeof vAny.requestVideoFrameCallback === 'function') {
          vAny.requestVideoFrameCallback(markReady);
        } else {
          liveVideo.addEventListener('playing', markReady, { once: true });
          liveVideo.addEventListener('loadeddata', markReady, { once: true });
        }
        // Failsafe so the summon can't wedge — but only dismiss onto a
        // video that actually HAS frames. Cold voice-bot joins can take
        // 15-20s; blind-firing at 6s dropped the overlay onto a white
        // rectangle while the bot audio said "Speaking" (David's
        // 2026-07-06 phone screenshot). Re-check every 2s, hard-stop
        // at ~30s total so a dead video track still ends the wait.
        let failsafe: ReturnType<typeof setTimeout>;
        let failsafeChecks = 0;
        const armFailsafe = (delay: number): void => {
          failsafe = setTimeout(() => {
            if (liveVideo.videoWidth > 0 || failsafeChecks >= 12) {
              markReady();
              return;
            }
            failsafeChecks += 1;
            armFailsafe(2000);
          }, delay);
        };
        armFailsafe(6000);
        cleanupLive = () => {
          done = true;
          clearTimeout(failsafe);
          liveVideo.removeEventListener('playing', markReady);
          liveVideo.removeEventListener('loadeddata', markReady);
        };
      } else {
        stopKeyer();
        bindLive(null);
        bits.liveReady.value = false;
      }
    }),
  );
  // The view can change mid-call (presence, layout switches): keying
  // follows the rule, never the moment the stream arrived.
  if (bits.keyLive) {
    disposers.push(
      effect(() => {
        const want = bits.keyLive!.value;
        if (!want && keyer) stopKeyer();
        else if (want && !keyer && bits.liveStream.value) startKeying();
      }),
    );
  }
  disposers.push(() => stopKeyer());

  // ---- tab-visibility resume (verbatim) ----
  const onVisible = (): void => {
    if (typeof document === 'undefined' || document.visibilityState !== 'visible') return;
    const stream = bits.liveStream.value;
    if (stream) {
      // A plain play() doesn't recover a stalled WebRTC decode —
      // re-assigning srcObject forces the element to re-render frames.
      liveVideo.srcObject = null;
      liveBackdrop.srcObject = null;
      bindLive(stream);
    }
    if (greeterVideo.paused && bits.greeterUrl.value && !bits.speaking.value) {
      playSafe(greeterVideo);
    }
  };
  document.addEventListener('visibilitychange', onVisible);
  disposers.push(() => document.removeEventListener('visibilitychange', onVisible));

  const dock = (name: AvatarDockName): void => {
    const anchor = docks.get(name);
    active = name; // remember intent even if the anchor registers later
    if (!anchor) return;
    if (el.parentElement === anchor) return;
    greeterVideo.className = GREETER_CLASS[name];
    anchor.appendChild(el);
    // Decode-hiccup recovery — same recipe as the visibility handler.
    // Deliberately does NOT touch liveReady (invariant I3): the frame
    // handshake already ran; the face re-paints from the live stream.
    const stream = bits.liveStream.value;
    if (stream) {
      liveVideo.srcObject = null;
      liveBackdrop.srcObject = null;
      bindLive(stream);
    }
    if (
      bits.greeterUrl.value &&
      !bits.speaking.value &&
      bits.greeterPaused?.value !== true &&
      !stream
    ) {
      playSafe(greeterVideo);
    }
  };

  return {
    el,
    registerDock(name, anchor) {
      docks.set(name, anchor);
      if (active === name && el.parentElement !== anchor) dock(name);
    },
    unregisterDock(name) {
      const anchor = docks.get(name);
      docks.delete(name);
      if (anchor && el.parentElement === anchor) anchor.removeChild(el);
    },
    dock,
    activeDock: () => active,
    dispose() {
      cleanupLive?.();
      for (const d of disposers) d();
      el.remove();
    },
  };
}
